import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { IntlContext } from '@/i18n';
import { IDriver } from '@/modules/driver/model/driver';
import { IPartner } from '@/modules/partner/model';
import { useGenderOptions } from '@/shared/hooks/useGenderOptions';
import { CountryCode } from '@/shared/models/country-code';
import { DescriptionList } from '@/shared/view/DescriptionList';

import { messages } from './chauffeurMessages';
import s from './styles.module.css';

interface IProps {
  partner: IPartner;
  driver: IDriver;
  className: string;
  children: React.ReactNode;
  onClickEdit: () => void;
}

const mapToDriverListValues = (partner: IPartner, driver: IDriver) => ({
  name: [partner.firstName, partner.middleName, partner.lastName].join(' '),
  birthday: driver.birthday,
  gender: driver.gender,
  phone: partner.phone,
  email: partner.email,
  licenseNumber: driver.licenseNumber,
  insurance: driver.ukNationalInsurance,
  address: driver.homeAddress,
  inn: partner.personalInn,
  licensingAuthority: driver.licensingAuthority,
});

type TTitleLine = [keyof ReturnType<typeof mapToDriverListValues>, string];

const skipEmpty = (item: TTitleLine | false): item is TTitleLine => item !== false;

const ChauffeurInfo = ({ className, driver, partner, children, onClickEdit }: IProps) => {
  const intl = useIntl();
  const { region } = useContext(IntlContext);
  // All options, so a stored value outside the country's list still gets a title
  const genderOptions = useGenderOptions();

  const chauffeurTitles = useMemo((): Array<TTitleLine> => {
    const allTitles: Array<TTitleLine | false> = [
      ['name', intl.formatMessage(messages.nameTitle)],
      ['birthday', intl.formatMessage(messages.birthdayTitle)],
      ['gender', intl.formatMessage(messages.genderTitle)],
      ['phone', intl.formatMessage(messages.phoneTitle)],
      ['email', intl.formatMessage(messages.emailTitle)],
      region === CountryCode.UnitedStates && [
        'licenseNumber',
        intl.formatMessage(messages.usDriverLicenseTitle),
      ],
      region === CountryCode.France && ['licenseNumber', intl.formatMessage(messages.frVtcTitle)],
      region === CountryCode.GreatBritain && [
        'licensingAuthority',
        intl.formatMessage(messages.gbLicensingAuthorityTitle),
      ],
      region === CountryCode.GreatBritain && [
        'licenseNumber',
        intl.formatMessage(messages.gbLicenceNumberTitle),
      ],
      region === CountryCode.GreatBritain && [
        'insurance',
        intl.formatMessage(messages.gbInsuranceTitle),
      ],
      region === CountryCode.Russia && ['inn', intl.formatMessage(messages.ruInnTitle)],
      ['address', intl.formatMessage(messages.addressTitle)],
    ];

    return allTitles.filter(skipEmpty);
  }, [intl, region]);

  return (
    <DescriptionList
      title={intl.formatMessage(messages.title)}
      className={className}
      values={useMemo(() => mapToDriverListValues(partner, driver), [partner, driver])}
      titles={chauffeurTitles}
      itemsForSelect={{ gender: genderOptions }}
      action={
        (region === CountryCode.GreatBritain || region === CountryCode.UnitedStates) &&
        driver.accessStatus === 'active' && (
          <span onClick={onClickEdit} className={s.editButton}>
            {intl.formatMessage(messages.editAction)}
          </span>
        )
      }
    >
      {children}
    </DescriptionList>
  );
};

export { ChauffeurInfo };
